import { BaseResponse } from '@kts-front/types';

import { ENDPOINTS } from 'configs/endpoints';
import { SubAgentEntity, subAgentSchema } from 'entities/SubAgent';
import { logApiErrWithSentry } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { notificationsStore, rootStore } from 'stores/global/RootStore';
import { SubAgentEditFieldsKey } from 'stores/global/RootStore/AppConfigStore';
import { EditFormStore, EditFormStoreOptions } from 'stores/local/shared/EntityEditPopupStore/EditFormStore';
import { FieldPhoneStore } from 'stores/local/shared/form/FieldPhoneStore';
import { FieldTextStore } from 'stores/local/shared/form/FieldTextStore';
import { validateEmail, validatePhoneNumber, validateIf } from 'utils/validators';

import { DefaultValues } from '../ClientEditFormStore';

type FieldsMap = {
  [SubAgentEditFieldsKey.phone]: FieldPhoneStore;
  [SubAgentEditFieldsKey.firstName]: FieldTextStore;
  [SubAgentEditFieldsKey.lastName]: FieldTextStore;
  [SubAgentEditFieldsKey.patronymic]: FieldTextStore;
  [SubAgentEditFieldsKey.email]: FieldTextStore;
};

export class SubAgentStore extends EditFormStore<DefaultValues, FieldsMap> {
  readonly fieldsMap: FieldsMap;

  private readonly _createRequest = apiStore.createRequest({
    url: ENDPOINTS.subAgent.create,
    method: 'POST',
  });

  constructor({ onSuccess }: EditFormStoreOptions) {
    super({ onSuccess });

    this.fieldsMap = {
      [SubAgentEditFieldsKey.phone]: new FieldPhoneStore({
        required: !rootStore.appConfigStore.logic.subAgentCreation.optionalFields.has(SubAgentEditFieldsKey.phone),
        shown: rootStore.appConfigStore.logic.subAgentCreation.shownFields.has(SubAgentEditFieldsKey.phone),
        validator: validatePhoneNumber(),
      }),
      [SubAgentEditFieldsKey.firstName]: new FieldTextStore({
        required: !rootStore.appConfigStore.logic.subAgentCreation.optionalFields.has(SubAgentEditFieldsKey.firstName),
        shown: rootStore.appConfigStore.logic.subAgentCreation.shownFields.has(SubAgentEditFieldsKey.firstName),
      }),
      [SubAgentEditFieldsKey.lastName]: new FieldTextStore({
        required: !rootStore.appConfigStore.logic.subAgentCreation.optionalFields.has(SubAgentEditFieldsKey.lastName),
        shown: rootStore.appConfigStore.logic.subAgentCreation.shownFields.has(SubAgentEditFieldsKey.lastName),
      }),
      [SubAgentEditFieldsKey.patronymic]: new FieldTextStore({
        required: !rootStore.appConfigStore.logic.subAgentCreation.optionalFields.has(SubAgentEditFieldsKey.patronymic),
        shown: rootStore.appConfigStore.logic.subAgentCreation.shownFields.has(SubAgentEditFieldsKey.patronymic),
      }),
      [SubAgentEditFieldsKey.email]: new FieldTextStore({
        required: !rootStore.appConfigStore.logic.subAgentCreation.optionalFields.has(SubAgentEditFieldsKey.email),
        shown: rootStore.appConfigStore.logic.subAgentCreation.shownFields.has(SubAgentEditFieldsKey.email),
        validator: validateIf(() => Boolean(this.fieldsMap[SubAgentEditFieldsKey.email].value), validateEmail()),
      }),
    };
  }

  reset(): void {
    super.reset();
  }

  init(defaultValues: DefaultValues): void {
    super.init(defaultValues);

    if (defaultValues.phone) {
      this.fieldsMap[SubAgentEditFieldsKey.phone].setValue(defaultValues.phone, { noTouch: true });
    }

    if (defaultValues.firstName) {
      this.fieldsMap[SubAgentEditFieldsKey.firstName].setValue(defaultValues.firstName, { noTouch: true });
    }

    if (defaultValues.lastName) {
      this.fieldsMap[SubAgentEditFieldsKey.lastName].setValue(defaultValues.lastName, { noTouch: true });
    }

    if (defaultValues.patronymic) {
      this.fieldsMap[SubAgentEditFieldsKey.patronymic].setValue(defaultValues.patronymic, { noTouch: true });
    }

    if (defaultValues.email) {
      this.fieldsMap[SubAgentEditFieldsKey.email].setValue(defaultValues.email, { noTouch: true });
    }
  }

  async submit(): Promise<void> {
    if (!this.validateFields()) {
      return;
    }

    const result = await this._createSubAgent();

    if (!result.isError) {
      this._options.onSuccess({ isCreated: true, createdSubAgent: result.data });
    }
  }

  private async _createSubAgent(): Promise<BaseResponse<SubAgentEntity>> {
    this.loadingStage.loading();

    const requestPayload = this.fieldsKeys.reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = this.fieldsMap[key].serverFormat;

      return acc;
    }, {});

    const response = await this._createRequest.call({
      data: requestPayload,
    });

    if (response.isError) {
      logApiErrWithSentry({
        endpoint: ENDPOINTS.subAgent.create,
        method: 'POST',
        requestPayload,
        response,
      });
      const fields = response.data?.data?.fields ?? [];

      if (fields.length > 0) {
        fields.forEach((f: { field: string; message: string }) => {
          this.setFieldSyntheticError(f.field, f.message);
        });
        notificationsStore.showError({ message: response.data?.data?.message, autoClose: 6000 });
      } else {
        notificationsStore.showErrorByResponse(response);
      }

      this.loadingStage.error();

      return { isError: true };
    }

    const parsed = subAgentSchema.safeParse(response.data);

    if (parsed.error) {
      this.loadingStage.error();
      notificationsStore.showError({ message: parsed.error.message });

      return { isError: true };
    }

    notificationsStore.showSuccess('Субагент успешно создан');

    this.loadingStage.success();

    return { isError: false, data: parsed.data };
  }
}
